import React, { createContext, useContext } from 'react';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';
import { useSupabaseAuth, type OAuthProvider } from '@memorai/auth/client';
import { getWebConfig } from '@memorai/config';
import { supabase } from '../lib/supabase';

interface AuthContextType {
    supabase: SupabaseClient;
    session: Session | null;
    user: User | null;
    loading: boolean;
    signInWithPassword: (email: string, password: string) => Promise<unknown>;
    signInWithOAuth: (provider: OAuthProvider, redirectTo?: string) => Promise<unknown>;
    signUp: (email: string, password: string) => Promise<unknown>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    supabase,
    session: null,
    user: null,
    loading: true,
    signInWithPassword: async () => { },
    signInWithOAuth: async () => { },
    signUp: async () => { },
    signOut: async () => { },
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const config = getWebConfig(import.meta.env);
    const auth = useSupabaseAuth({
        supabaseUrl: config.supabaseUrl,
        supabaseAnonKey: config.supabaseAnonKey,
        supabaseClient: supabase,
        isDev: import.meta.env.DEV,
        skipAuth: config.skipAuth,
    });

    return (
        <AuthContext.Provider value={auth}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
